import React, { useCallback, useEffect, useState } from "react";
import { UserPlus, Loader2, ShieldCheck, Clock, Ban, RefreshCw, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const ROLE_LABEL = {
  trial_user: "Trial — full tools",
  campaign_user: "Campaign — one-off run",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

const StatusPill = ({ status }) => {
  const map = {
    active: "bg-emerald-100 text-emerald-700",
    expired: "bg-zinc-100 text-zinc-500",
    revoked: "bg-rose-100 text-rose-700",
  };
  return (
    <span className={`text-[11px] font-semibold uppercase tracking-[0.08em] px-2 py-0.5 rounded ${map[status] || "bg-zinc-100 text-zinc-600"}`}>
      {status}
    </span>
  );
};

export default function DemoAccessPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", name: "", role: "campaign_user", days: 14 });
  const [granting, setGranting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/demo-access");
      setItems(data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load demo access");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const grant = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) { toast.error("Enter the prospect's email"); return; }
    setGranting(true);
    try {
      const { data } = await api.post("/admin/demo-access/grant", {
        email: form.email.trim(), name: form.name.trim(), role: form.role, days: Number(form.days),
      });
      const prov = data.provisioning || {};
      if (prov.sent) toast.success(`Access granted — magic link emailed to ${form.email.trim()}`);
      else if (prov.provisioned) toast.success("Access granted — user provisioned (magic link not sent; check Auth0 config)");
      else toast.success("Access granted on Ekselens — Auth0 magic link not sent (Auth0 not configured)");
      setForm({ email: "", name: "", role: "campaign_user", days: 14 });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Grant failed");
    } finally { setGranting(false); }
  };

  const revoke = async (entry) => {
    if (!window.confirm(`Revoke access for ${entry.value}? Their login is cut immediately.`)) return;
    try {
      await api.post(`/admin/demo-access/${entry.id}/revoke`);
      toast.success(`Access revoked for ${entry.value}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Revoke failed");
    }
  };

  return (
    <div data-testid="demo-access-panel" className="max-w-5xl">
      <div className="flex items-start gap-3 mb-6">
        <div className="mt-1 h-10 w-10 rounded-lg bg-[#7737F6]/10 flex items-center justify-center flex-shrink-0">
          <ShieldCheck className="h-5 w-5 text-[#7737F6]" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-zinc-900">Demo access</h2>
          <p className="text-sm text-zinc-500 mt-0.5 max-w-2xl">
            Grant a prospect time-boxed, magic-link login so they can run a demo. The access tier carries into ERIO too (one shared identity). Revoke any time — the login is cut immediately.
          </p>
        </div>
      </div>

      {/* Grant form */}
      <form onSubmit={grant} className="rounded-xl border border-zinc-200 bg-white p-5 mb-8" data-testid="demo-access-grant-form">
        <div className="flex items-center gap-2 mb-4 text-zinc-700">
          <UserPlus className="h-4 w-4 text-[#7737F6]" />
          <span className="text-[12px] font-semibold uppercase tracking-[0.12em]">Grant new demo access</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <label className="block text-xs text-zinc-500 mb-1">Prospect email</label>
            <input
              type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="prospect@company.com" data-testid="demo-access-email"
              className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-[15px] focus:outline-none focus:ring-2 focus:ring-[#7737F6]/30"
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Name (optional)</label>
            <input
              type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Jane Smith" data-testid="demo-access-name"
              className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-[15px] focus:outline-none focus:ring-2 focus:ring-[#7737F6]/30"
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Duration (working days)</label>
            <input
              type="number" min={1} max={60} value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })}
              data-testid="demo-access-days"
              className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-[15px] focus:outline-none focus:ring-2 focus:ring-[#7737F6]/30"
            />
          </div>
        </div>
        <div className="flex flex-wrap items-end justify-between gap-3 mt-3">
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Access level</label>
            <select
              value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
              data-testid="demo-access-role"
              className="border border-zinc-300 rounded-lg px-3 py-2 text-[15px] bg-white focus:outline-none focus:ring-2 focus:ring-[#7737F6]/30"
            >
              <option value="campaign_user">Campaign — one-off run (lightest)</option>
              <option value="trial_user">Trial — full tools for the window</option>
            </select>
          </div>
          <button
            type="submit" disabled={granting} data-testid="demo-access-grant-submit"
            className="inline-flex items-center gap-2 rounded-lg bg-[#7737F6] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#6629d9] transition-colors disabled:opacity-60"
          >
            {granting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            {granting ? "Granting…" : "Grant & send magic link"}
          </button>
        </div>
      </form>

      {/* Access overview */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-zinc-700">
          <Clock className="h-4 w-4 text-[#7737F6]" />
          <span className="text-[12px] font-semibold uppercase tracking-[0.12em]">Current temporary access</span>
        </div>
        <button type="button" onClick={load} className="text-zinc-500 hover:text-black text-[12px]" data-testid="demo-access-refresh">
          <RefreshCw className="inline h-3 w-3 mr-1" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-zinc-500 text-sm py-8"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-zinc-500 text-sm py-8 border border-dashed border-zinc-200 rounded-xl text-center" data-testid="demo-access-empty">
          No temporary access granted yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-zinc-200">
          <table className="w-full text-sm" data-testid="demo-access-table">
            <thead className="bg-zinc-50 text-zinc-500 text-[11px] uppercase tracking-[0.08em]">
              <tr>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Access level</th>
                <th className="text-left px-4 py-3">Expires</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-zinc-100" data-testid={`demo-access-row-${it.id}`}>
                  <td className="px-4 py-3 text-zinc-800">
                    {it.value}
                    {it.display_name ? <span className="block text-xs text-zinc-400">{it.display_name}</span> : null}
                  </td>
                  <td className="px-4 py-3 text-zinc-600">{ROLE_LABEL[it.platform_role] || it.platform_role}</td>
                  <td className="px-4 py-3 text-zinc-600">{fmtDate(it.trial_expires_at)}</td>
                  <td className="px-4 py-3"><StatusPill status={it.status} /></td>
                  <td className="px-4 py-3 text-right">
                    {it.status !== "revoked" && (
                      <button
                        type="button" onClick={() => revoke(it)} data-testid={`demo-access-revoke-${it.id}`}
                        className="inline-flex items-center gap-1 text-rose-600 hover:text-rose-700 text-[13px] font-medium"
                      >
                        <Ban className="h-3.5 w-3.5" /> Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
